import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, Button, FormField, Input, IntegerInput, Select } from '@shared-ui/components';
import { adminApi, type AgentPersonaSpec } from '../../api/adminApi';

const EMPTY: AgentPersonaSpec = {
  id: '', app: 'student', hands: 'browser', role: '', board: 'JEE', grade: 11,
  subjects: [], personality: '', device: 'laptop', skillLevel: 'average',
  patience: 'medium', effort: 'moderate', entry: 'login', freVariants: false, goalTemplate: '',
};

interface Props {
  /** Existing spec to edit; omit to create a new persona. */
  initial?: AgentPersonaSpec;
  onClose: () => void;
}

/** Add / edit a persona. On create the id is editable; on edit it's fixed (partition key). */
export function PersonaForm({ initial, onClose }: Props) {
  const qc = useQueryClient();
  const isEdit = !!initial;
  const [spec, setSpec] = useState<AgentPersonaSpec>(initial ?? EMPTY);
  const [subjectsText, setSubjectsText] = useState((initial?.subjects ?? []).join(', '));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof AgentPersonaSpec>(k: K, v: AgentPersonaSpec[K]) =>
    setSpec(s => ({ ...s, [k]: v }));

  async function save() {
    setError(null);
    const payload: AgentPersonaSpec = {
      ...spec,
      id: spec.id.trim(),
      subjects: subjectsText.split(',').map(s => s.trim()).filter(Boolean),
    };
    if (!payload.id || !payload.role.trim() || !payload.goalTemplate.trim()) {
      setError('id, role and goal are required.');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) await adminApi.updateAgentPersona(payload.id, payload);
      else await adminApi.createAgentPersona(payload);
      qc.invalidateQueries({ queryKey: ['agent-personas'] });
      qc.invalidateQueries({ queryKey: ['agent-persona', payload.id] });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={isEdit ? `Edit persona · ${initial!.id}` : 'Add persona'}
      footer={
        <div className="pa-form-actions">
          {error && <span className="pa-form-error">{error}</span>}
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create'}
          </Button>
        </div>
      }
    >
      <div className="pa-form-grid">
        <FormField label="ID" required>
          <Input value={spec.id} disabled={isEdit} placeholder="jee-phys-11-avg"
            onChange={e => set('id', e.target.value)} />
        </FormField>
        <FormField label="Role" required>
          <Input value={spec.role} placeholder="JEE Physics student, class 11"
            onChange={e => set('role', e.target.value)} />
        </FormField>
        <FormField label="Board">
          <Input value={spec.board} onChange={e => set('board', e.target.value)} />
        </FormField>
        <FormField label="Grade">
          <IntegerInput className="ui-input" value={spec.grade}
            onChange={v => { if (v != null) set('grade', v); }} />
        </FormField>
        <FormField label="Subjects (comma-separated)">
          <Input value={subjectsText} placeholder="physics, maths"
            onChange={e => setSubjectsText(e.target.value)} />
        </FormField>
        <FormField label="Device">
          <Select value={spec.device} onChange={e => set('device', e.target.value)}>
            {['laptop', 'phone', 'tablet'].map(o => <option key={o} value={o}>{o}</option>)}
          </Select>
        </FormField>
        <FormField label="App">
          <Select value={spec.app} onChange={e => set('app', e.target.value)}>
            {['student', 'teacher'].map(o => <option key={o} value={o}>{o}</option>)}
          </Select>
        </FormField>
        <FormField label="Hands">
          <Select value={spec.hands} onChange={e => set('hands', e.target.value)}>
            {['browser', 'api'].map(o => <option key={o} value={o}>{o}</option>)}
          </Select>
        </FormField>
        <FormField label="Entry">
          <Select value={spec.entry ?? 'login'} onChange={e => set('entry', e.target.value)}>
            {['login', 'guest'].map(o => <option key={o} value={o}>{o}</option>)}
          </Select>
        </FormField>
        <FormField label="Skill level">
          <Select value={spec.skillLevel} onChange={e => set('skillLevel', e.target.value)}>
            {['weak', 'average', 'strong'].map(o => <option key={o} value={o}>{o}</option>)}
          </Select>
        </FormField>
        <FormField label="Patience">
          <Select value={spec.patience} onChange={e => set('patience', e.target.value)}>
            {['low', 'medium', 'high'].map(o => <option key={o} value={o}>{o}</option>)}
          </Select>
        </FormField>
        <FormField label="Effort">
          <Select value={spec.effort ?? 'moderate'} onChange={e => set('effort', e.target.value)}>
            {['lazy', 'moderate', 'diligent'].map(o => <option key={o} value={o}>{o}</option>)}
          </Select>
        </FormField>
        <FormField label="Rotate first-run variants">
          <label className="pa-form-check">
            <input type="checkbox" checked={!!spec.freVariants}
              onChange={e => set('freVariants', e.target.checked)} />
            <span className="pa-muted">only meaningful for guest/FRE personas</span>
          </label>
        </FormField>
      </div>
      <FormField label="Personality" className="pa-form-wide">
        <textarea className="pa-input pa-form-textarea" rows={3} value={spec.personality}
          onChange={e => set('personality', e.target.value)} />
      </FormField>
      <FormField label="Goal" required className="pa-form-wide">
        <textarea className="pa-input pa-form-textarea" rows={3} value={spec.goalTemplate}
          onChange={e => set('goalTemplate', e.target.value)} />
      </FormField>
    </Modal>
  );
}
